// src/components/SocialMediaPostGenerator.js
import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select'; // Added React Select
import { httpsCallable } from 'firebase/functions';
import { functionsInstance, db } from '../../firebase/firebase';
import { collection, onSnapshot, query, orderBy, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { Loader2, Sparkles, Facebook, Twitter, Instagram, Archive, Copy, Save, Trash2, Info, Video } from 'lucide-react';

// Accept an initialProduct prop
const SocialMediaPostGenerator = ({ currentUser, userTptProducts, initialProduct }) => {
    // --- STATE MANAGEMENT ---
    const [selectedProduct, setSelectedProduct] = useState(initialProduct || null);
    const [postGoal, setPostGoal] = useState('Drive Sales');
    const [customHook, setCustomHook] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const [tone, setTone] = useState('Friendly');
    const [postOptions, setPostOptions] = useState({
        includeHashtags: true,
        includeLink: true,
        includeEmojis: true,
        addCallToAction: true,
    });

    const [generatedPosts, setGeneratedPosts] = useState(null);
    const [activePlatform, setActivePlatform] = useState('Facebook');

    const [savedPosts, setSavedPosts] = useState([]);
    const [isSaving, setIsSaving] = useState({});

    // --- EFFECT HOOKS ---

    useEffect(() => {
        if (initialProduct && initialProduct.id !== selectedProduct?.id) {
            setSelectedProduct(initialProduct);
        } else if (!selectedProduct && userTptProducts && userTptProducts.length > 0) {
            setSelectedProduct(userTptProducts[0]);
        }
    }, [userTptProducts, initialProduct, selectedProduct]);

    useEffect(() => {
        if (selectedProduct && currentUser) {
            // Check if it's a real product ID (not general store post) before querying
            if (selectedProduct.id === 'general_store_post') {
                setSavedPosts([]);
                return;
            }

            const savedPostsRef = collection(db, `users/${currentUser.uid}/products/${selectedProduct.id}/savedSocialPosts`);
            const q = query(savedPostsRef, orderBy('savedAt', 'desc'));

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const posts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setSavedPosts(posts);
            });

            return () => unsubscribe();
        } else {
            setSavedPosts([]);
        }
    }, [selectedProduct, currentUser]);


    // --- DATA PREPARATION (Dropdown) ---

    // 1. Prepare Options: Sort Alphabetically & Add General Option
    const productOptions = useMemo(() => {
        if (!userTptProducts) return [];

        // Sort products alphabetically
        const sortedProducts = [...userTptProducts].sort((a, b) =>
            (a.productTitle || "").localeCompare(b.productTitle || "")
        );

        // Map to options
        const options = sortedProducts.map(p => ({
            value: p.id,
            label: p.productTitle
        }));

        // Add General Store Post at the end (or top, depending on preference)
        options.push({ value: 'general_store_post', label: 'General Store Post' });

        return options;
    }, [userTptProducts]);

    // 2. Custom Styles for React Select (Matches Rank Tracker)
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: 'white',
            borderColor: state.isFocused ? '#0ea5e9' : '#cbd5e1',
            borderRadius: '0.5rem',
            padding: '0.35rem', // Match height of other inputs roughly
            boxShadow: state.isFocused ? '0 0 0 2px rgba(14, 165, 233, 0.2)' : '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            fontSize: '0.875rem',
            '&:hover': { borderColor: state.isFocused ? '#0ea5e9' : '#94a3b8' },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#0ea5e9' : state.isFocused ? '#f0f9ff' : 'white',
            color: state.isSelected ? 'white' : '#1e293b',
            fontSize: '0.875rem',
            cursor: 'pointer',
        }),
        singleValue: (provided) => ({ ...provided, color: '#1e293b' }),
        placeholder: (provided) => ({ ...provided, color: '#94a3b8' }),
        menu: (provided) => ({ ...provided, zIndex: 50 }), // Ensure dropdown sits on top
    };

    // 3. Handle Change
    const handleProductChange = (selectedOption) => {
        if (!selectedOption) {
            setSelectedProduct(null);
            return;
        }

        if (selectedOption.value === 'general_store_post') {
            // Create a dummy object for the General Store Post state
            setSelectedProduct({
                id: 'general_store_post',
                productTitle: 'General Store Post',
                tptUrl: null
            });
        } else {
            const product = userTptProducts.find(p => p.id === selectedOption.value);
            setSelectedProduct(product);
        }
    };


    // --- HANDLER FUNCTIONS ---

    const handleOptionChange = (e) => {
        const { name, checked } = e.target;
        setPostOptions(prev => ({ ...prev, [name]: checked }));
    };

    // Helper to remove asterisks if the AI accidentally includes them
    const cleanText = (text) => {
        if (!text) return "";
        // Removes ** and * markers but keeps the text inside them
        return text.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
    };

    const handleGenerate = async () => {
        if (!selectedProduct) {
            setError("Please select a product first.");
            return;
        }
        setIsLoading(true);
        setError('');
        setGeneratedPosts(null);

        try {
            const generateFunction = httpsCallable(functionsInstance, 'generateSocialMediaPosts');
            const result = await generateFunction({
                productId: selectedProduct.id,
                goal: postGoal,
                hook: customHook,
                tone: tone,
                options: postOptions,
                productUrl: selectedProduct.tptUrl || null,
            });

            setGeneratedPosts(result.data);
            setActivePlatform('Facebook');
        } catch (err) {
            console.error("Error generating social media posts:", err);
            setError(err.message || "Could not generate posts at this time.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSavePost = async (platform, postContent) => {
        if (!currentUser || !selectedProduct) return;

        // Don't try to save for General Store Post (no ID to attach to in DB structure)
        if (selectedProduct.id === 'general_store_post') {
            alert("Saving is not currently supported for General Store Posts.");
            return;
        }

        const cleanedContent = cleanText(postContent);
        setIsSaving(prev => ({ ...prev, [postContent]: true }));

        try {
            const savedPostsRef = collection(db, `users/${currentUser.uid}/products/${selectedProduct.id}/savedSocialPosts`);
            await addDoc(savedPostsRef, {
                platform,
                content: cleanedContent,
                savedAt: new Date(),
            });
        } catch (err) {
            console.error("Error saving post:", err);
            setError("Could not save post.");
        } finally {
            setIsSaving(prev => ({ ...prev, [postContent]: false }));
        }
    };

    const handleDeletePost = async (postId) => {
        if (!currentUser || !selectedProduct) return;
        try {
            const postRef = doc(db, `users/${currentUser.uid}/products/${selectedProduct.id}/savedSocialPosts`, postId);
            await deleteDoc(postRef);
        } catch (err) {
             console.error("Error deleting post:", err);
             setError("Could not delete post.");
        }
    };

    const copyToClipboard = (text) => {
        const cleanedContent = cleanText(text);
        navigator.clipboard.writeText(cleanedContent);
    };

    // --- RENDER ---

    const renderPlatformIcon = (platform) => {
        const iconProps = { className: "w-5 h-5" };
        switch(platform) {
            case 'Facebook': return <Facebook {...iconProps} />;
            case 'Instagram': return <Instagram {...iconProps} />;
            case 'Pinterest': return (
                <svg {...iconProps} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                   <path d="M12 1.00002C5.92487 1.00002 1 5.92489 1 12C1 17.1353 4.41737 21.2483 9.07625 22.0494C9.25375 22.0792 9.385 21.9363 9.385 21.761V19.4581C9.385 19.243 9.25375 19.0494 9.05625 18.9789C7.75375 18.5226 6.88375 17.2726 6.88375 15.8351C6.88375 13.6289 8.78 11.8338 10.9763 11.8338C12.3837 11.8338 13.2337 12.7225 13.2337 13.8475C13.2337 14.8818 12.595 16.2025 12.0875 17.2368C11.5388 18.3518 12.445 19.2501 13.5163 19.2501C15.9388 19.2501 17.1163 16.6526 17.1163 14.39C17.1163 11.0826 14.8925 8.79991 11.695 8.79991C8.01875 8.79991 5.37875 11.5338 5.37875 14.7113C5.37875 15.7051 5.80375 16.5938 6.3325 17.0601C6.445 17.1634 6.465 17.3384 6.40375 17.461C6.1725 17.9789 5.93125 18.7899 5.86 19.0494C5.81875 19.2331 5.6775 19.3364 5.5025 19.2801C3.56625 18.6364 2.15875 16.6326 2.15875 14.39C2.15875 8.92379 6.58125 4.50004 12 4.50004C17.4187 4.50004 21.8412 8.92379 21.8412 14.39C21.8412 18.9138 18.3975 22.5801 13.9237 22.8876C13.4362 22.9383 13.2537 22.6209 13.2537 22.331V20.217C13.2537 19.9889 13.345 19.7801 13.5163 19.6888C14.075 19.3813 14.49 18.6676 14.49 17.9033C14.49 16.5126 13.2037 15.6851 13.2037 14.7313C13.2037 13.5557 14.1512 12.8319 14.9787 12.8319C16.925 12.8319 18.2825 14.617 18.2825 17.2669C18.2825 19.4121 16.5325 22.1869 12.915 22.1869C12.635 22.1869 12.3138 22.1768 12 22.1567C18.0751 22.1567 23 17.2318 23 11.1567C23 5.08164 18.0751 0.156738 12 0.156738" fill="#E60023"/>
                </svg>
            );
            case 'X': return <Twitter {...iconProps} />;
            default: return null;
        }
    };


    return (
        <div className="space-y-8 font-sans">
            {/* --- Generator Section --- */}
            <section className="relative bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-slate-200">
                <a
                    href="https://youtu.be/jM1UCwIp6zY?si=Rkl1FCn4N5Nh7Yt-"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-6 right-6 flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"
                >
                    <Video size={14} />
                    Watch Tutorial
                </a>
                <div>
                    <h2 className="text-xl font-semibold text-sky-700 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sparkles h-6 w-6 mr-2 text-sky-600">
                            <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path>
                            <path d="M20 3v4"></path><path d="M22 5h-4"></path>
                            <path d="M4 17v2"></path><path d="M5 18H3"></path>
                        </svg>
                        Social Media Post Generator
                    </h2>
                    <p className="text-xs text-slate-500 mt-2 mb-6">
                        Create scroll-stopping captions for Facebook, Instagram, X (Twitter), and Pinterest. Select a product, choose your vibe, and let AI do the heavy lifting.
                    </p>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                        <label htmlFor="product-select-social" className="block text-sm font-medium text-slate-700 mb-1">1. Select Product</label>
                        {/* REPLACED Standard Select with React Select */}
                        <Select
                            id="product-select-social"
                            options={productOptions}
                            value={selectedProduct ? { value: selectedProduct.id, label: selectedProduct.productTitle } : null}
                            onChange={handleProductChange}
                            styles={customStyles}
                            placeholder={userTptProducts?.length === 0 ? "Add a product first..." : "Select or type to search..."}
                            isDisabled={!userTptProducts || userTptProducts.length === 0}
                            isSearchable
                        />
                    </div>
                    <div>
                        <label htmlFor="post-goal" className="block text-sm font-medium text-slate-700 mb-1">2. Choose Goal</label>
                        <select
                            id="post-goal"
                            value={postGoal}
                            onChange={e => setPostGoal(e.target.value)}
                            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 text-sm"
                        >
                            <option>Drive Sales</option>
                            <option>Announce a Freebie</option>
                            <option>Share a Tip / Value</option>
                            <option>Promote a Blog Post</option>
                            <option>Build Community</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="tone-select" className="block text-sm font-medium text-slate-700 mb-1">3. Select Tone</label>
                        <select
                            id="tone-select"
                            value={tone}
                            onChange={e => setTone(e.target.value)}
                            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 text-sm"
                        >
                            <option>Friendly</option>
                            <option>Professional</option>
                            <option>Witty</option>
                            <option>Trendy / Viral</option>
                            <option>Enthusiastic</option>
                            <option>Inspirational</option>
                        </select>
                    </div>
                </div>

                <div className="mt-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">4. Post Enhancements</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.keys(postOptions).map((option) => (
                            <label key={option} className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    name={option}
                                    checked={postOptions[option]}
                                    onChange={handleOptionChange}
                                    className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                />
                                <span className="text-sm text-slate-600">
                                    {option.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                    <div>
                        <label htmlFor="hook-input" className="block text-sm font-medium text-slate-700 mb-1">5. Add a Hook (Optional)</label>
                        <input
                            id="hook-input"
                            type="text"
                            value={customHook}
                            onChange={e => setCustomHook(e.target.value)}
                            placeholder="e.g., 'Stop grading papers on weekends!'"
                            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 text-sm"
                        />
                    </div>
                    <button
                        onClick={handleGenerate}
                        disabled={isLoading || !selectedProduct}
                        className="w-full flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold p-3 rounded-lg shadow-md transition-colors disabled:bg-slate-400"
                    >
                        {isLoading ? <Loader2 className="animate-spin" /> : <Sparkles className="w-5 h-5" />}
                        <span>{isLoading ? 'Generating...' : 'Generate Posts'}</span>
                    </button>
                </div>
                {error && <p className="text-red-600 text-sm mt-4">{error}</p>}
            </section>

            <section className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div className="flex items-start">
                    <div className="p-1.5 bg-sky-100 rounded-lg mr-3 mt-1">
                        <Info className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                        <h3 className="text-md font-bold text-slate-800">Platform Tips</h3>
                        <ul className="mt-2 space-y-1 text-xs text-slate-600 list-disc list-inside">
                            <li><span className="font-semibold">Facebook:</span> Focus on community. Ask questions to drive comments.</li>
                            <li><span className="font-semibold">Instagram:</span> "Link in bio" is key. Use emojis and break up text with spacing.</li>
                            <li><span className="font-semibold">Pinterest:</span> Use keywords! Teachers search here like Google.</li>
                            <li><span className="font-semibold">X (Twitter):</span> Keep it punchy. Short, sharp value adds work best.</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* --- Results Section --- */}
            {generatedPosts && (
                <section className="animate-fade-in">
                    <div className="border-b border-slate-200">
                        <nav className="-mb-px flex space-x-4 overflow-x-auto" aria-label="Tabs">
                            {Object.keys(generatedPosts).map(platform => (
                                <button
                                    key={platform}
                                    onClick={() => setActivePlatform(platform)}
                                    className={`flex items-center gap-2 whitespace-nowrap py-3 px-4 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
                                        activePlatform === platform
                                            ? 'bg-white text-sky-600 border-sky-500'
                                            : 'bg-transparent text-slate-500 border-transparent hover:text-sky-600'
                                    }`}
                                >
                                    {renderPlatformIcon(platform)} {platform}
                                </button>
                            ))}
                        </nav>
                    </div>

                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {(generatedPosts[activePlatform] || []).map((post, index) => {
                            const isPinterest = activePlatform === 'Pinterest';

                            let pinTitle = '';
                            let pinDescription = '';
                            let displayContent = '';

                            if (isPinterest && post && typeof post === 'object') {
                                pinTitle = post.title || '';
                                pinDescription = post.description || '';
                                displayContent = cleanText(pinDescription);
                            } else {
                                displayContent = cleanText(post);
                            }

                            const contentToCopy = isPinterest
                                ? `${pinTitle}${pinTitle && pinDescription ? '\n\n' : ''}${pinDescription}`
                                : (typeof post === 'string' ? post : '');

                            return (
                                <div
                                    key={index}
                                    className="bg-white p-5 rounded-xl shadow-lg border border-slate-200 space-y-4 flex flex-col"
                                >
                                    {isPinterest && pinTitle && (
                                        <div className="mb-1">
                                            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                                                Pin Title
                                            </p>
                                            <p className="text-sm font-semibold text-slate-800">
                                                {pinTitle}
                                            </p>
                                        </div>
                                    )}

                                    {/* Added break-words here to fix URL overflow */}
                                    <p className="flex-grow text-sm text-slate-700 leading-relaxed whitespace-pre-wrap break-words">
                                        {displayContent}
                                    </p>
                                    <div className="flex-shrink-0 flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
                                        <button
                                            onClick={() => copyToClipboard(contentToCopy)}
                                            className="text-xs font-semibold text-slate-500 hover:text-sky-600 flex items-center gap-1.5"
                                        >
                                            <Copy className="w-4 h-4" />
                                            Copy
                                        </button>
                                        <button
                                            onClick={() => handleSavePost(activePlatform, contentToCopy)}
                                            disabled={isSaving[contentToCopy]}
                                            className="text-xs font-semibold text-slate-500 hover:text-sky-600 flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            {isSaving[contentToCopy]
                                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                                : <Save className="w-4 h-4" />}
                                            Save
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* --- Saved Posts Library --- */}
            <section className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-slate-200">
                <div className="flex items-center">
                    <div className="p-2 bg-teal-100 rounded-lg mr-4">
                        <Archive className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Saved Post Library</h2>
                        <p className="text-sm text-slate-500">
                            Your saved posts for:{' '}
                            <span className="font-semibold text-slate-700">
                                {selectedProduct?.productTitle || 'No Product Selected'}
                            </span>
                        </p>
                    </div>
                </div>
                <div className="mt-6">
                    {savedPosts.length > 0 ? (
                        <div className="space-y-3">
                            {savedPosts.map(post => (
                                <div
                                    key={post.id}
                                    className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex items-center justify-between gap-4"
                                >
                                    <p className="text-sm text-slate-600 truncate pr-4">
                                        {cleanText(post.content)}
                                    </p>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                                            {renderPlatformIcon(post.platform)} {post.platform}
                                        </span>
                                        <button
                                            onClick={() => copyToClipboard(post.content)}
                                            className="text-xs font-semibold text-slate-500 hover:text-sky-600 flex items-center gap-1.5"
                                        >
                                            <Copy className="w-4 h-4" />
                                            Copy
                                        </button>
                                        <button
                                            onClick={() => handleDeletePost(post.id)}
                                            className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1.5"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-sm text-slate-500 py-8">
                            You haven't saved any posts for this product yet.
                        </p>
                    )}
                </div>
            </section>
        </div>
    );
};

export default SocialMediaPostGenerator;